import React from "react";
import { Box, Typography, Grid, Divider, Paper, Stack } from "@mui/material";
import DetailItem from "../common/DetailItem";
import { ShiftResponseDto } from "../../shared/dtos/shift/ShiftResponseDto";
import { formatPlainNumber } from "../../utils/appUtils";

interface ShiftBreakdownProps {
  shift: ShiftResponseDto | null;
}

const ShiftBreakdown: React.FC<ShiftBreakdownProps> = ({ shift }) => {
  if (!shift) return null;

  return (
    <Box>
      <Grid container spacing={2}>
        <DetailItem label="Cashier" value={shift.cashier} />
        <DetailItem label="Branch" value={shift.branchName} />
        <DetailItem
          label="Start Time"
          value={new Date(shift.startTime).toLocaleString()}
        />
        <DetailItem
          label="End Time"
          value={
            shift.endTime
              ? new Date(shift.endTime).toLocaleString()
              : "Active (In Progress)"
          }
        />
      </Grid>

      <Divider sx={{ my: 2 }}>
        <Typography variant="overline">Cash Register</Typography>
      </Divider>
      <Grid container spacing={2}>
        <DetailItem
          label="Opening Float"
          value={formatPlainNumber(shift.openingFloat)}
        />
        <DetailItem
          label="Total Cash Inflow"
          value={formatPlainNumber(shift.totalCashInflow)}
        />
        <DetailItem
          label="Total Cash Outflow"
          value={formatPlainNumber(shift.totalCashOutflow)}
        />
        <DetailItem label="Expected Cash in Till">
          <Typography
            variant="body1"
            sx={{ fontWeight: "bold", color: "secondary.main" }}
          >
            {formatPlainNumber(shift.expectedCash)}
          </Typography>
        </DetailItem>
      </Grid>

      <Divider sx={{ my: 2 }}>
        <Typography variant="overline">Sales Overview</Typography>
      </Divider>
      {shift.sale ? (
        <Grid container spacing={2}>
          <DetailItem
            label="Gross Sales"
            value={formatPlainNumber(shift.sale.salesTotal)}
          />
          <DetailItem
            label="Total Discount"
            value={formatPlainNumber(shift.sale.totalDiscount)}
          />
          <DetailItem
            label="Total Tax"
            value={formatPlainNumber(shift.sale.totalTax)}
          />
          <DetailItem
            label="Sales Profit"
            value={formatPlainNumber(shift.sale.salesProfit)}
          />
        </Grid>
      ) : (
        <Typography variant="body2" sx={{ color: "text.secondary" }}>
          No sales recorded during this shift.
        </Typography>
      )}

      {/* EXPENSES BREAKDOWN */}
      {shift.expenses && shift.expenses.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="overline">Expenses Summary</Typography>
          </Divider>
          <Stack spacing={1}>
            {shift.expenses.map((exp) => (
              <Paper
                key={exp.id}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                    {exp.category}
                  </Typography>
                  <Typography
                    variant="caption"
                    sx={{ color: "text.secondary" }}
                  >
                    {exp.description} • By: {exp.processedBy}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  sx={{ color: "error.main", fontWeight: "bold" }}
                >
                  {formatPlainNumber(exp.amount)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </>
      )}

      {/* PAYMENTS BREAKDOWN */}
      {shift.payments && shift.payments.length > 0 && (
        <>
          <Divider sx={{ my: 2 }}>
            <Typography variant="overline">Payment Methods Summary</Typography>
          </Divider>
          <Stack spacing={1}>
            {shift.payments.map((p, idx) => (
              <Paper
                key={idx}
                variant="outlined"
                sx={{
                  p: 1.5,
                  display: "flex",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                  {p.paymentMethodName} ({p.type})
                </Typography>
                <Typography
                  variant="body2"
                  sx={{ color: "success.main", fontWeight: "bold" }}
                >
                  {formatPlainNumber(p.netAmount)}
                </Typography>
              </Paper>
            ))}
          </Stack>
        </>
      )}
    </Box>
  );
};

export default ShiftBreakdown;
